import React, { useState, useContext, useMemo, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Switch,
  Input,
  Drawer,
  Form,
  message,
  Table,
  Tabs,
  Select,
  Tag,
  Checkbox,
  Segmented,
} from "antd";
import { EditOutlined, DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import useLang from "../../../hooks/useLang";
import SectionHeader from "../SectionHeader";
import { openDialog } from "../../../library/helpers";
import { DIALOG_TYPES } from "../../hoc/Dialog/dialog";
import CustomSwitch from "../../commonComponents/switch";
import {
  getAdminSelectedLocation,
  getCompanyId,
  getLocationId,
} from "../../../redux/slices/adminSelectedLocation";
import {
  CREATE_AI_SUGGESTION,
  UPDATE_AI_SUGGESTION,
  DELETE_AI_SUGGESTION,
} from "../../../graphql/mutation/mutation";
import LocationBackButton from "../location/LocationBackButton";
import { GET_AI_SUGGESTION_QUICK_TIP, GET_DEPARTMENTS } from "../../../graphql/query";
import { normalizeChannels } from "../../../helpers/quickTipsAndTonesHelper";

// DnD helpers using Row Context pattern
const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const SortableRow = props => {
  const { style: propStyle, "data-row-key": rowKey, ...restProps } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowKey });

  const style = {
    ...propStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...restProps} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

// Adding type Enum
export const ADDING_TYPE = Object.freeze({
  TIPS: "QUICK_TIPS",
  TONES: "QUICK_TONES",
  LENGTH: "LENGTH",
  ADDITIONAL_NOTES: "ADDITIONAL_NOTES",
});

const ADDITIONAL_NOTES_TIP = "Allow to add additional notes";

// Communication channel options - add new options here as needed
const COMMUNICATION_CHANNEL_OPTIONS = [
  { label: "Text", value: "Text" },
  { label: "Email", value: "Email" },
];

// Channel filter options for Segmented component
const CHANNEL_FILTER_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Text (SMS)", value: "Text" },
  { label: "Email", value: "Email" },
];

// Reusable Communication Channel Filter component
const CommunicationChannelFilter = ({ value, onChange }) => (
  <div className="my-10">
    <h4 className="text-sm font-semibold text-gray-800 mb-1">Communication Channel</h4>
    <p className="text-sm text-gray-500 mb-3">
      This filter applies across Quick tips, Tone, and Length settings
    </p>
    <Segmented
      className="!mt-3"
      options={CHANNEL_FILTER_OPTIONS}
      value={value}
      onChange={onChange}
    />
  </div>
);

const CustomizeAiSuggestions = ({ onBack }) => {
  const [lang] = useLang();
  // Must match one of the Tabs `key`s below ("company" | "department") —
  // a key that matches neither leaves every tab unselected on open.
  const [activeTab, setActiveTab] = useState("company");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [allowAdditionalNotes, setAllowAdditionalNotes] = useState(false);
  const [additionalNotesId, setAdditionalNotesId] = useState(null);
  const [savingAdditionalNotes, setSavingAdditionalNotes] = useState(false);
  const [quickTips, setQuickTips] = useState([]);
  const [tones, setTones] = useState([]);
  const [lengths, setLengths] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addingType, setAddingType] = useState(ADDING_TYPE.TIPS);
  const [channelFilter, setChannelFilter] = useState("All");
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();

  const selectedLocationId = useSelector(getLocationId);
  const selectedLocation = useSelector(getAdminSelectedLocation);

  const companyId = useSelector(getCompanyId);
  // applicationUseCaseId is fixed to 4 for create/fetch
  const applicationUseCaseId = 4; // in future make this dynamic.

  // Load departments for the selected location
  const { data: departmentsData, loading: departmentsLoading } = useQuery(GET_DEPARTMENTS, {
    variables: {
      locationId: selectedLocationId ? Number(selectedLocationId) : undefined,
      companyId: companyId || undefined,
    },
    skip: !selectedLocationId || !companyId,
    fetchPolicy: "network-only",
  });

  const departments = useMemo(
    () => departmentsData?.getDepartments?.filter(dept => dept.isActive) || [],
    [departmentsData]
  );

  // Set first department as default when departments load and in department tab
  useEffect(() => {
    if (activeTab === "department" && departments.length > 0 && !selectedDepartmentId) {
      setSelectedDepartmentId(departments[0].id);
    }
  }, [departments, activeTab, selectedDepartmentId]);

  // Load existing tips from API and keep UI in sync on refetch
  const {
    data,
    loading: queryLoading,
    refetch: refetchTips,
  } = useQuery(GET_AI_SUGGESTION_QUICK_TIP, {
    variables: {
      companyId: companyId || undefined,
      locationIds: selectedLocationId ? [Number(selectedLocationId)] : undefined,
      departmentIds:
        activeTab === "department" && selectedDepartmentId ? [selectedDepartmentId] : null,
      applicationUseCaseId,
      communicationChannel: channelFilter !== "All" ? channelFilter : undefined,
    },
    fetchPolicy: "network-only",
    // Skip query if:
    // - Missing required params (companyId or selectedLocationId)
    // - In department tab but no department selected
    skip:
      !companyId || !selectedLocationId || (activeTab === "department" && !selectedDepartmentId),
    notifyOnNetworkStatusChange: true,
  });

  // Refetch when tab or department selection changes
  useEffect(() => {
    if (companyId && selectedLocationId) {
      refetchTips();
    }
  }, [activeTab, selectedDepartmentId, companyId, selectedLocationId, channelFilter, refetchTips]);

  // Keep UI in sync with latest query data (runs on initial fetch and refetch)
  useEffect(() => {
    // Clear data when in department tab but no department selected
    if (activeTab === "department" && !selectedDepartmentId) {
      setQuickTips([]);
      setTones([]);
      setLengths([]);
      setAllowAdditionalNotes(false);
      setAdditionalNotesId(null);
      return;
    }

    const list = data?.getAiSuggestionQuickTip || [];
    if (!Array.isArray(list) || list.length === 0) {
      setQuickTips([]);
      setTones([]);
      setLengths([]);
      setAllowAdditionalNotes(false);
      setAdditionalNotesId(null);
      return;
    }

    const tips = list
      .filter(it => it?.suggestionType === ADDING_TYPE.TIPS)
      .map((it, idx) => ({
        id: it.id,
        title: it.tip,
        communicationChannels: normalizeChannels(it.communicationChannels),
        enabled: it?.isActive,
        order: idx + 1,
        type: ADDING_TYPE.TIPS,
      }));
    const toneItems = list
      .filter(it => it?.suggestionType === ADDING_TYPE.TONES)
      .map((it, idx) => ({
        id: it.id,
        title: it.tip,
        communicationChannels: normalizeChannels(it.communicationChannels),
        enabled: it?.isActive,
        order: idx + 1,
        type: ADDING_TYPE.TONES,
      }));
    const lengthItems = list
      .filter(it => it?.suggestionType === ADDING_TYPE.LENGTH)
      .map((it, idx) => ({
        id: it.id,
        title: it.tip,
        communicationChannels: normalizeChannels(it.communicationChannels),
        enabled: it?.isActive,
        order: idx + 1,
        type: ADDING_TYPE.LENGTH,
      }));
    const additionalNotesItem = list.find(
      it => it?.suggestionType === ADDING_TYPE.ADDITIONAL_NOTES
    );
    setQuickTips(tips);
    setTones(toneItems);
    setLengths(lengthItems);
    setAllowAdditionalNotes(Boolean(additionalNotesItem?.isActive));
    setAdditionalNotesId(additionalNotesItem?.id ?? null);
  }, [data, activeTab, selectedDepartmentId]);

  const [createSuggestion, { loading: creating }] = useMutation(CREATE_AI_SUGGESTION);
  const [updateSuggestion, { loading: updating }] = useMutation(UPDATE_AI_SUGGESTION);
  const [deleteSuggestion] = useMutation(DELETE_AI_SUGGESTION);

  // Combine loading states for table
  const tableLoading = queryLoading;

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper to get type message
  const getTypeMessage = type => {
    if (type === ADDING_TYPE.TIPS) return "Quick tip";
    if (type === ADDING_TYPE.TONES) return "Tone";
    if (type === ADDING_TYPE.LENGTH) return "Length";
    return "Item";
  };

  const handleToggleItem = async (type, record) => {
    const { id, enabled, title } = record;
    const nextActive = !enabled;
    try {
      await updateSuggestion({
        variables: {
          id: Number(id),
          tip: title ?? undefined,
          isActive: nextActive,
        },
      });
      await refetchTips();
      const typeMsg = getTypeMessage(type);
      message.success(`${typeMsg} ${nextActive ? "enabled" : "disabled"}`);
    } catch (err) {
      const typeMsg = getTypeMessage(type);
      message.error(
        err?.message || `Failed to ${nextActive ? "enable" : "disable"} ${typeMsg.toLowerCase()}`
      );
    }
  };

  const handleEditItem = item => {
    setEditingItem({ ...item });
    // Prefill form with existing values
    editForm.setFieldsValue({
      title: item.title,
      communicationChannels: item.communicationChannels,
    });
    setEditModalVisible(true);
  };

  const handleToggleAdditionalNotes = async checked => {
    const prev = allowAdditionalNotes;
    setAllowAdditionalNotes(checked);
    setSavingAdditionalNotes(true);
    try {
      if (additionalNotesId) {
        await updateSuggestion({
          variables: {
            id: Number(additionalNotesId),
            isActive: checked,
            tip: ADDITIONAL_NOTES_TIP,
          },
        });
      } else {
        // Create the ADDITIONAL_NOTES record on first toggle
        await createSuggestion({
          variables: {
            applicationUseCaseId,
            companyId,
            description: "",
            isActive: checked,
            locationId: Number(selectedLocationId),
            suggestionType: ADDING_TYPE.ADDITIONAL_NOTES,
            tip: ADDITIONAL_NOTES_TIP,
            departmentId: activeTab === "department" ? selectedDepartmentId : null,
          },
        });
      }
      await refetchTips();
      message.success(`Additional notes ${checked ? "enabled" : "disabled"}`);
    } catch (err) {
      setAllowAdditionalNotes(prev);
      message.error(err?.message || `Failed to ${checked ? "enable" : "disable"} additional notes`);
    } finally {
      setSavingAdditionalNotes(false);
    }
  };

  const onEditFinish = async values => {
    const updated = { ...editingItem, ...values };
    try {
      await updateSuggestion({
        variables: {
          id: Number(updated.id),
          tip: values.title,
          communicationChannels: values.communicationChannels || [],
        },
      });

      await refetchTips();
      setEditModalVisible(false);
      setEditingItem(null);
      const typeMsg = getTypeMessage(updated.type);
      message.success(`${typeMsg} updated successfully`);
    } catch (err) {
      const typeMsg = getTypeMessage(editingItem?.type);
      message.error(err?.message || `Failed to update ${typeMsg.toLowerCase()}`);
    }
  };

  const handleDeleteItem = async (type, id) => {
    try {
      await deleteSuggestion({ variables: { id: Number(id) } });
      await refetchTips();
      const typeMsg = getTypeMessage(type);
      message.success(`${typeMsg} deleted successfully`);
    } catch (err) {
      const typeMsg = getTypeMessage(type);
      message.error(err?.message || `Failed to delete ${typeMsg.toLowerCase()}`);
    }
  };

  const onAddFinish = async values => {
    try {
      const variables = {
        applicationUseCaseId: 4,
        companyId,
        communicationChannels: values?.communicationChannels || [],
        isActive: true,
        locationId: Number(selectedLocationId),
        suggestionType: addingType,
        tip: values?.title,
        departmentId: activeTab === "department" ? selectedDepartmentId : null,
      };

      await createSuggestion({ variables });

      // Refresh list after creating new item (tips, tones, and lengths)
      await refetchTips();

      setAddModalVisible(false);
      setEditingItem(null);
      addForm.resetFields();
      const typeMsg = getTypeMessage(addingType);
      message.success(`${typeMsg} added successfully`);
    } catch (err) {
      const typeMsg = getTypeMessage(addingType);
      message.error(err?.message || `Failed to add ${typeMsg.toLowerCase()}`);
    }
  };

  // Handle drag end
  const handleDragEnd = (event, type) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const updateItems = setter => {
        setter(items => {
          const oldIndex = items.findIndex(item => item.id === active.id);
          const newIndex = items.findIndex(item => item.id === over.id);
          const newItems = arrayMove(items, oldIndex, newIndex);

          // Update order property
          return newItems.map((item, index) => ({
            ...item,
            order: index + 1,
          }));
        });
      };

      if (type === ADDING_TYPE.TIPS) {
        updateItems(setQuickTips);
      } else if (type === ADDING_TYPE.TONES) {
        updateItems(setTones);
      } else if (type === ADDING_TYPE.LENGTH) {
        updateItems(setLengths);
      }
      message.success("Order updated successfully");
    }
  };

  // Helper to get table title based on type
  const getTableTitle = type => {
    if (type === ADDING_TYPE.TIPS) return "Quick tips";
    if (type === ADDING_TYPE.TONES) return "Tones";
    if (type === ADDING_TYPE.LENGTH) return "Length";
    return "Items";
  };

  // Table columns with DragHandle and Switch controls
  const getTableColumns = type => [
    // {
    //   key: "controls",
    //   width: 60,
    //   align: "center",
    //   render: () => <DragHandle />,
    // },
    {
      title: getTableTitle(type),
      dataIndex: "title",
      key: "title",
      width: 180,
      render: text => <p className="text-sm-font-40">{text}</p>,
    },
    {
      title: "Communication Channel",
      dataIndex: "communicationChannels",
      key: "communicationChannels",
      width: 180,
      render: value => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return <span className="text-sm-font-40">--</span>;
        }
        // Handle both array and string values for backward compatibility
        const channels = Array.isArray(value) ? value : [value];
        return (
          <div className="flex gap-1 flex-wrap">
            {channels.map(channel => {
              const isText = channel.toLowerCase() === "text";
              const isEmail = channel.toLowerCase() === "email";
              if (isText) {
                return (
                  <Tag key={channel} color="blue">
                    Text
                  </Tag>
                );
              }
              if (isEmail) {
                return (
                  <Tag key={channel} color="magenta">
                    Email
                  </Tag>
                );
              }
              return <Tag key={channel}>{channel}</Tag>;
            })}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {/* <Switch
            checked={record?.enabled}
            onChange={() => handleToggleItem(type, record)}
            size="small"
            loading={updating || creating}
          /> */}
          <Button
            type="text"
            icon={<EditOutlined style={{ color: "#00000073" }} />}
            onClick={() => handleEditItem({ ...record, type })}
            className="p-1"
          />
          <Button
            type="text"
            icon={<DeleteOutlined style={{ color: "#00000073" }} />}
            onClick={() => {
              const typeMsg = getTypeMessage(type);
              const header = `Delete ${typeMsg}`;
              const name = record?.title ? `"${record.title}"` : lang?.thisItem || "this item";
              openDialog(DIALOG_TYPES.NEW_CONFIRM_DIALOG, {
                dialogHeader: header,
                headerLine1: lang?.areYouSureDelete
                  ? `${lang.areYouSureDelete} ${name}?`
                  : `Are you sure you want to delete ${name}?`,
                headerLine2: lang?.thisActionCannotBeUndone || "This action cannot be undone.",
                okBtnText: lang?.delete || "Delete",
                onOk: async () => {
                  await handleDeleteItem(type, record.id);
                },
                centered: true,
                isDanger: true,
              });
            }}
            className="p-1"
          />
        </div>
      ),
    },
  ];

  const renderItemsTable = (items, type, title, disabled = false) => {
    // Sort items by order property
    const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
      <div className="mb-8" style={{ maxWidth: 700 }}>
        {/* Header Row with Light Grey Background */}
        <div>
          <div className="flex mb-4 items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">{title}</h3>
            <Button
              type="primary"
              disabled={disabled}
              onClick={() => {
                setAddingType(type);
                setEditingItem({ title: "", communicationChannels: [], type });
                // Reset and prefill add form
                addForm.resetFields();
                setAddModalVisible(true);
              }}
            >
              Add {getTypeMessage(type)}
            </Button>
          </div>
        </div>

        {/* Draggable Table */}
        <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={event => handleDragEnd(event, type)}
          >
            <SortableContext
              items={sortedItems.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                components={{
                  body: {
                    row: SortableRow,
                  },
                }}
                columns={getTableColumns(type)}
                dataSource={sortedItems}
                rowKey="id"
                pagination={false}
                size="small"
                bordered
                className="border-0 drag-table"
                showHeader
                loading={tableLoading}
                rowClassName="hover:bg-gray-50"
              />
            </SortableContext>
          </DndContext>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Header */}
      <LocationBackButton onBack={onBack} />

      <SectionHeader
        title="Customize AI Suggestion"
        description={lang?.Settings?.customizeAiSuggestionsInfo}
      />

      {/* Main Content */}
      <div className="mt-4">
        <Tabs
          activeKey={activeTab}
          onChange={key => {
            setActiveTab(key);
            // Leaving the department tab drops its selection.
            if (key === "company") {
              setSelectedDepartmentId(null);
            }
          }}
          items={[
            {
              key: "company",
              label: "Location level",
              children: (
                <div>
                  {/* Allow additional notes toggle */}
                  {/* <div className="my-8">
                    <CustomSwitch
                      checked={allowAdditionalNotes}
                      onChange={handleToggleAdditionalNotes}
                      size="small"
                      labelPosition="post"
                      label="Allow to add additional notes"
                      loading={creating || updating}
                    />
                  </div> */}

                  {/* Communication Channel Filter */}
                  <CommunicationChannelFilter value={channelFilter} onChange={setChannelFilter} />

                  {/* Quick tips section */}
                  {renderItemsTable(quickTips, ADDING_TYPE.TIPS, "Quick Tips")}

                  {/* Tones section */}
                  {renderItemsTable(tones, ADDING_TYPE.TONES, "Tone")}

                  {/* Length section */}
                  {renderItemsTable(lengths, ADDING_TYPE.LENGTH, "Length")}
                </div>
              ),
            },
            {
              key: "department",
              label: "Department level",
              children: (
                <div>
                  {/* Department selector */}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Select Department <span className="text-red-500">*</span>
                    </label>
                    <Select
                      style={{ width: 300 }}
                      placeholder="Select a department"
                      value={selectedDepartmentId}
                      onChange={value => setSelectedDepartmentId(value)}
                      loading={departmentsLoading}
                      disabled={departments.length === 0}
                      options={departments.map(dept => ({
                        label: dept.name,
                        value: dept.id,
                      }))}
                    />
                    {departments.length === 0 && !departmentsLoading && (
                      <p className="text-sm text-gray-500 mt-1">
                        No departments available for this location
                      </p>
                    )}
                  </div>

                  {/* Allow additional notes toggle */}
                  {/* <div className="my-8">
                    <CustomSwitch
                      checked={allowAdditionalNotes}
                      onChange={handleToggleAdditionalNotes}
                      size="small"
                      labelPosition="post"
                      label="Allow to add additional notes"
                      loading={creating || updating}
                      disabled={departments.length === 0}
                    />
                  </div> */}

                  {/* Communication Channel Filter */}
                  <CommunicationChannelFilter value={channelFilter} onChange={setChannelFilter} />

                  {/* Quick tips section */}
                  {renderItemsTable(
                    quickTips,
                    ADDING_TYPE.TIPS,
                    "Quick Tips",
                    departments.length === 0
                  )}

                  {/* Tones section */}
                  {renderItemsTable(tones, ADDING_TYPE.TONES, "Tone", departments.length === 0)}

                  {/* Length section */}
                  {renderItemsTable(
                    lengths,
                    ADDING_TYPE.LENGTH,
                    "Length",
                    departments.length === 0
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Edit Drawer */}
      {editModalVisible && (
        <Drawer
          title={`Edit ${getTypeMessage(editingItem?.type)}`}
          open={editModalVisible}
          width={480}
          onClose={() => {
            setEditModalVisible(false);
            setEditingItem(null);
          }}
          footer={
            <div className="flex justify-start gap-2">
              <Button type="primary" loading={updating} onClick={() => editForm.submit()}>
                Save
              </Button>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
            </div>
          }
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={onEditFinish}
            initialValues={{
              title: editingItem?.title,
              communicationChannels: editingItem?.communicationChannels,
            }}
          >
            <Form.Item
              name="title"
              label={<span className="form-field-label">{getTypeMessage(editingItem?.type)}</span>}
              rules={[{ required: true, message: "Please enter a title" }]}
            >
              <Input placeholder="Enter title" />
            </Form.Item>
            <Form.Item
              className="!mt-5"
              name="communicationChannels"
              label={<span className="form-field-label">Communication Channel</span>}
              rules={[
                {
                  required: true,
                  message: "Please select at least one communication channel",
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(
                        new Error("Please select at least one communication channel")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Checkbox.Group className="flex flex-col gap-2">
                {COMMUNICATION_CHANNEL_OPTIONS.map(option => (
                  <Checkbox key={option.value} value={option.value}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Form>
        </Drawer>
      )}

      {/* Add Drawer */}
      {addModalVisible && (
        <Drawer
          title={`Add ${getTypeMessage(addingType)}`}
          open={addModalVisible}
          width={480}
          onClose={() => {
            setAddModalVisible(false);
            setEditingItem(null);
          }}
          footer={
            <div className="flex justify-start gap-2">
              <Button type="primary" loading={creating} onClick={() => addForm.submit()}>
                Save
              </Button>
              <Button
                onClick={() => {
                  setAddModalVisible(false);
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
            </div>
          }
        >
          <Form form={addForm} layout="vertical" onFinish={onAddFinish}>
            <Form.Item
              name="title"
              label={<span className="form-field-label">{getTypeMessage(addingType)}</span>}
              rules={[{ required: true, message: "Please enter a title" }]}
            >
              <Input placeholder="Enter title" />
            </Form.Item>
            <Form.Item
              name="communicationChannels"
              label={<span className="form-field-label">Communication Channel</span>}
              className="!mt-5"
              rules={[
                {
                  required: true,
                  message: "Please select at least one communication channel",
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(
                        new Error("Please select at least one communication channel")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Checkbox.Group className="flex flex-col gap-2">
                {COMMUNICATION_CHANNEL_OPTIONS.map(option => (
                  <Checkbox key={option.value} value={option.value}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Form>
        </Drawer>
      )}
    </div>
  );
};

export default CustomizeAiSuggestions;
